import React, { useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { createPortal } from 'react-dom';
import Icon from 'components/ui/icon';

const RequestProgressBar = ({ state, variant, isFirst, isLast, fillTop, fillBottom, label }) => {
  const [isHovered, setIsHovered] = useState(false);
  const anchorRef = useRef(null);

  const [anchorRect, setAnchorRect] = useState(null);
  const showTooltip = !!label && isHovered && !!anchorRect && typeof document !== 'undefined';

  const updateAnchorRect = () => {
    const el = anchorRef.current;
    if (!el) return;
    setAnchorRect(el.getBoundingClientRect());
  };

  useEffect(() => {
    if (!isHovered) return;
    updateAnchorRect();

    window.addEventListener('scroll', updateAnchorRect, true);
    window.addEventListener('resize', updateAnchorRect);
    return () => {
      window.removeEventListener('scroll', updateAnchorRect, true);
      window.removeEventListener('resize', updateAnchorRect);
    };
  }, [isHovered]);

  const tooltipStyle = useMemo(() => {
    if (!anchorRect) return null;
    const left = Math.round(anchorRect.right + 10);
    const top = Math.round(anchorRect.top + anchorRect.height / 2 - 14);
    return { position: 'fixed', left: `${left}px`, top: `${top}px` };
  }, [anchorRect]);

  return (
    <span
      ref={anchorRef}
      className={classNames('request-progress', {
        'is-active': state === 'active',
        'is-done': state === 'done',
        'is-invalid': state === 'invalid',
        'is-in-progress': state === 'in-progress',
        'is-todo': state === 'todo',
        'is-disabled': state === 'disabled',
        'is-base': variant === 'base',
        'is-sub': variant === 'sub',
        'is-first': isFirst,
        'is-last': isLast,
      })}
      onMouseEnter={
        label
          ? () => {
              setIsHovered(true);
              updateAnchorRect();
            }
          : undefined
      }
      onMouseLeave={label ? () => setIsHovered(false) : undefined}
      aria-hidden={label ? undefined : true}
      aria-label={label || undefined}
    >
      {!isFirst && (
        <span
          className={classNames('request-progress__line request-progress__line--top', {
            'is-filled': fillTop === 'filled',
            'is-error': fillTop === 'error',
          })}
        />
      )}
      {!isLast && (
        <span
          className={classNames('request-progress__line request-progress__line--bottom', {
            'is-filled': fillBottom === 'filled',
            'is-error': fillBottom === 'error',
          })}
        />
      )}
      <span className="request-progress__dot">
        {state === 'done' && <Icon name="check-step" className="request-progress__dot-icon" />}
        {state === 'invalid' && <Icon name="road-sign-warning" className="request-progress__dot-icon" />}
      </span>
      {showTooltip &&
        createPortal(
          <div className="sc-styled-popover" style={{ ...tooltipStyle, padding: '6px 8px' }}>
            {label}
          </div>,
          document.body,
        )}
    </span>
  );
};

RequestProgressBar.propTypes = {
  state: PropTypes.oneOf(['active', 'done', 'invalid', 'in-progress', 'todo', 'disabled']),
  variant: PropTypes.oneOf(['base', 'sub']),
  isFirst: PropTypes.bool,
  isLast: PropTypes.bool,
  fillTop: PropTypes.oneOf(['filled', 'error', null]),
  fillBottom: PropTypes.oneOf(['filled', 'error', null]),
  label: PropTypes.string,
};

RequestProgressBar.defaultProps = {
  state: 'todo',
  variant: 'sub',
  isFirst: false,
  isLast: false,
  fillTop: null,
  fillBottom: null,
  label: null,
};

export default RequestProgressBar;
